import { Link, useNavigate } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Youtube,
} from "lucide-react";

import { decryptPayload, isAndroidAppWebView } from "../helpers/utils";
import soc2Logo from "../../assets/new_ui_images/soc2-type2-badge.png";
import epLogo from "../../assets/new_ui_images/equity-protect-logo-full.png";

const socialLinks = [
  {
    name: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/equity.protect/",
  },
  {
    name: "Facebook",
    icon: Facebook,
    href: "https://www.facebook.com/people/EquityProtect/100090626499488/",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    href: "https://www.linkedin.com/company/equity-protect",
  },
  {
    name: "YouTube",
    icon: Youtube,
    href: "https://www.youtube.com/@equityprotect",
  },
];

const footerLinks = {
  company: [
    { name: "About Us", href: "/about" },
    { name: "Press", href: "/press" },
    { name: "Blog", href: "/blog" },
  ],
  resources: [
    { name: "How It Works", href: "/how-it-works" },
    { name: "Pricing", href: "/pricing", allow_county_code: true },
    { name: "Legislative Map", href: "/scorecard" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
  ],
};

export const Footer = ({ isServiceProvider = false }) => {
  const navigate = useNavigate();
  const is_scrp_hp = localStorage.getItem("scrp_hp");
  const countyCode = decryptPayload(localStorage.getItem("county-code"));
  const isAndroidApp =
    typeof window !== "undefined" &&
    (isAndroidAppWebView() ||
      decryptPayload(localStorage.getItem("isMobile")) === "android");

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 px-2">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src={epLogo}
              alt="EquityProtect"
              className="h-14 w-auto mb-6 brightness-0 invert"
            />
            <div className="space-y-3">
              <a
                href="tel:+18882132959"
                className="flex items-center gap-3 text-primary-foreground/70 hover:text-secondary transition-colors"
              >
                <Phone className="h-4 w-4" />
                (888) 213-2959
              </a>
              <a
                href="mailto:information@EquityProtect.com"
                className="flex items-center gap-3 text-primary-foreground/70 hover:text-secondary transition-colors"
              >
                <Mail className="h-4 w-4" />
                information@EquityProtect.com
              </a>
              <div className="flex items-start gap-3 text-primary-foreground/70">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                5566 Longley Lane Suite B<br />
                Reno NV 89511
              </div>
            </div>

            {/* Social Media Links */}
            <div className="flex items-center gap-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="text-primary-foreground/70 hover:text-secondary transition-colors"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {!isServiceProvider && !isAndroidApp && (
            <>
              {/* Company Links */}
              <div>
                <h3 className="font-semibold mb-4">Company</h3>
                <ul className="space-y-3">
                  {footerLinks.company.map((link) => (
                    <li key={link.name}>
                      <a
                        href={link.href}
                        className="text-primary-foreground/70 hover:text-secondary transition-colors"
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources Links */}
              <div>
                <h3 className="font-semibold mb-4">Resources</h3>
                <ul className="space-y-3">
                  {footerLinks.resources.map((link) => (
                    <li key={link.name}>
                      {link.href.startsWith("/") && !link.href.includes("#") ? (
                        <Link
                          to={link.href}
                          className="text-primary-foreground/70 hover:text-secondary transition-colors"
                          onClick={(e) => {
                            if (
                              parseInt(is_scrp_hp) === 1 &&
                              countyCode &&
                              link.allow_county_code
                            ) {
                              e.preventDefault();
                              navigate(
                                link.href === "/"
                                  ? `/county-recorder/${countyCode}`
                                  : `/${countyCode}${link.href}`,
                              );
                            }
                          }}
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-primary-foreground/70 hover:text-secondary transition-colors"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h3 className="font-semibold mb-4">Legal</h3>
                <ul className="space-y-3">
                  {footerLinks.legal.map((link) => (
                    <li key={link.name}>
                      <a
                        href={link.href}
                        rel="noopener noreferrer"
                        target="_blank"
                        className="text-primary-foreground/70 hover:text-secondary transition-colors"
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>

                {/* SOC 2 Logo */}
                <img
                  src={soc2Logo}
                  alt="SOC 2 Type II Certified"
                  className="h-20 w-auto mt-6"
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-12 pt-8 border-t border-primary-foreground/10">
          <p className="text-sm text-primary-foreground/50 text-center md:text-left">
            © {new Date().getFullYear()} EquityProtect. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
